"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.math = void 0;
const create_fn_1 = require("../utils/create-fn");
const get_arg_1 = require("../utils/get-arg");
const unsupported_fn_1 = require("../utils/unsupported-fn");
/*
## fn.math.round and fn.math.roundup

Round a number to the nearest integer. Half a number is rounded away from zero.

### Arguments

1. (number) The number to round.

Returns the rounded number. (number)
*/
const round = (0, create_fn_1.createFn)("fn.math.round", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return roundHalfUp(value);
});
/*
## fn.math.rounddown

Round a number to the nearest integer. Half a number is rounded toward zero.

### Arguments

1. (number) The number to round.

Returns the rounded number. (number)
*/
const rounddown = (0, create_fn_1.createFn)("fn.math.rounddown", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return roundHalfDown(value);
});
/*
## fn.math.max

Find the maximum number.

### Arguments

1-Infinity. (number) The numbers to compare.

Returns the maximum number. (number)
*/
const max = (0, create_fn_1.createFn)("fn.math.max", (args, context) => {
    const maxArgs = [];
    for (let i = 1; i <= args.length; i += 1) {
        const value = (0, get_arg_1.getArgNumberOrUndefined)(args, i, context);
        if (value !== undefined) {
            maxArgs.push(value);
        }
    }
    if (maxArgs.length === 0) {
        return undefined;
    }
    return Math.max(...maxArgs);
});
/*
## fn.math.min

Find the minimum number.

### Arguments

1-Infinity. (number) The numbers to compare.

Returns the minimum number. (number)
*/
const min = (0, create_fn_1.createFn)("fn.math.min", (args, context) => {
    const minArgs = [];
    for (let i = 1; i <= args.length; i += 1) {
        const value = (0, get_arg_1.getArgNumberOrUndefined)(args, i, context);
        if (value !== undefined) {
            minArgs.push(value);
        }
    }
    if (minArgs.length === 0) {
        return undefined;
    }
    return Math.min(...minArgs);
});
/*
## fn.math.decimalround

Round a number to a specific number of decimal places.

### Arguments

1. (number) The number to round.
2. (number) The number of decimal places to round to.
3. (string) The rounding method. One of "up", "down", or "even". Defaults to "even".
   1. "up" - Half a number is rounded away from zero.
   2. "down" - Half a number is rounded toward zero.
   3. "even" - Half a number is rounded to the nearest even number.

Returns the rounded number. (string)
*/
function mapDecimalRoundMethod(method) {
    switch (method) {
        case "up":
            return roundHalfUp;
        case "down":
            return roundHalfDown;
        case "even":
            return roundHalfToEven;
    }
    const [representation, type] = (0, get_arg_1.argDetails)(method);
    throw new Error(`fn.math.decimalround: Expected the 3rd arg to be one of "up", "down", or "even", instead "${representation}" was received which is of type ${type}`);
}
const decimalround = (0, create_fn_1.createFn)("fn.math.decimalround", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return "";
    }
    const decimals = (0, get_arg_1.getArgNumber)(args, 2, context);
    const method = (0, get_arg_1.getArgStringOrUndefined)(args, 3, context) || "even";
    const precision = Math.pow(10, decimals);
    return (mapDecimalRoundMethod(method)(value * precision) / precision).toFixed(decimals);
});
/*
## fn.math.asymmetricround

Round a number to the nearest integer. Half a number is rounded toward Infinity
(This also means away from negative Infinity).

### Arguments

1. (number) The number to round.

Returns the rounded number. (number)
*/
const asymmetricround = (0, create_fn_1.createFn)("fn.math.asymmetricround", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.round(value);
});
/*
## fn.math.log

Get the natural logarithm of a number.

### Arguments

1. (number) The number to get the logarithm of.

Returns the natural logarithm of the number. (number)
*/
const log = (0, create_fn_1.createFn)("fn.math.log", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.log(value);
});
/*
## fn.math.ceiling

Round a number up to the nearest integer.

### Arguments

1. (number) The number to round up.

Returns the rounded number. (number)
*/
const ceiling = (0, create_fn_1.createFn)("fn.math.ceiling", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.ceil(value);
});
/*
## fn.math.floor

Round a number down to the nearest integer.

### Arguments

1. (number) The number to round down.

Returns the rounded number. (number)
*/
const floor = (0, create_fn_1.createFn)("fn.math.floor", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.floor(value);
});
const haversine = (0, unsupported_fn_1.unsupportedFn)("fn.math.haversine");
/*
## fn.math.pow

Raise a number to a power.

### Arguments

1. (number) The number to raise.
2. (number) The power to raise to.

Returns the number raised to the power. (number)
*/
const pow = (0, create_fn_1.createFn)("fn.math.pow", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    const power = (0, get_arg_1.getArgNumber)(args, 2, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.pow(value, power);
});
/*
## fn.math.sqrt

Get the square root of a number.

### Arguments

1. (number) The number to get the square root of.

Returns the square root of the number. (number)
*/
const sqrt = (0, create_fn_1.createFn)("fn.math.sqrt", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    if (value === undefined) {
        return undefined;
    }
    return Math.sqrt(value);
});
/*
## fn.math.mod

Get the modulus of a number.

### Arguments

1. (number) The number to get the modulus of.
2. (number) The modulus to use.

Returns the modulus of the number. (number)
*/
const mod = (0, create_fn_1.createFn)("fn.math.mod", (args, context) => {
    const value = (0, get_arg_1.getArgNumberOrUndefined)(args, 1, context);
    const modulus = (0, get_arg_1.getArgNumberOrUndefined)(args, 2, context);
    if (value === undefined || modulus === undefined || modulus === 0) {
        return undefined;
    }
    return value % modulus;
});
exports.math = {
    round,
    roundup: round,
    rounddown,
    max,
    min,
    decimalround,
    asymmetricround,
    log,
    ceiling,
    floor,
    haversine,
    pow,
    sqrt,
    mod,
};
// Create a "round half up" function that mimics Java.
// From Wikipedia: Some programming languages (such as Java and Python) use
// "half up" to refer to round half away from zero rather than round half
// toward positive infinity.
function roundHalfUp(num) {
    if (num > -0.5) {
        return Math.floor(num + 0.5);
    }
    return Math.ceil(num - 0.5);
}
// Create a "round half down" function that mimics Java.
// From Wikipedia: Some programming languages (such as Java and Python) use
// "half down" to refer to round half toward zero rather than round half
// toward negative infinity.
function roundHalfDown(num) {
    if (num >= 0.5) {
        return Math.ceil(num - 0.5);
    }
    return Math.floor(num + 0.5);
}
function roundHalfToEven(x) {
    const r = Math.round(x);
    // Ends in .5?
    if (Math.abs(x) % 1 === 0.5) {
        return r % 2 === 0 ? r : r - 1;
    }
    return r;
}
//# sourceMappingURL=math.js.map